import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Calendar, FileText, BedDouble, 
  Receipt, Bell, LogOut, Menu, X, Activity, BarChart3, FlaskConical,
  ClipboardList, Pill, History, Mic, Shield, UserCog, Settings, Package,
  Stethoscope, MessageSquare, MessageCircle, TrendingUp, DollarSign, UserPlus,
  TestTube, Send, Palette, QrCode, FileJson
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useBranding } from '@/App';
import BreakGlassButton from '@/components/BreakGlassButton';

// Role-based navigation configuration
const NAVIGATION_CONFIG = {
  // Admin navigation
  admin: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Quick Registration', icon: QrCode, path: '/quick-registration' },
    { name: 'FHIR Viewer', icon: FileJson, path: '/fhir-viewer' },
    { name: 'User Management', icon: UserCog, path: '/user-management' },
    { name: 'Visit Management', icon: ClipboardList, path: '/visits' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'Medical Records', icon: FileText, path: '/medical-records' },
    { name: 'Prescriptions', icon: Pill, path: '/prescriptions' },
    { name: 'Lab Orders', icon: TestTube, path: '/lab-orders' },
    { name: 'Lab Entries', icon: FlaskConical, path: '/lab-entries' },
    { name: 'Admissions', icon: BedDouble, path: '/admissions' },
    { name: 'Discharge Summary', icon: FileText, path: '/discharge-summary' },
    { name: 'Billing', icon: Receipt, path: '/billing' },
    { name: 'Fixed Charges', icon: DollarSign, path: '/fixed-charges' },
    { name: 'Discharge Analytics', icon: BarChart3, path: '/discharge-analytics' },
    { name: 'Referral Triage', icon: Activity, path: '/referral-triage' },
    { name: 'Referring Doctors', icon: Stethoscope, path: '/referring-doctors' },
    { name: 'Referral Analytics', icon: TrendingUp, path: '/referral-analytics' },
    { name: 'Message Templates', icon: MessageSquare, path: '/message-templates' },
    { name: 'Send Message', icon: Send, path: '/send-message' },
    { name: 'Communication Logs', icon: MessageCircle, path: '/communication-logs' },
    { name: 'Provider Management', icon: UserPlus, path: '/provider-management' },
    { name: 'Security Alerts', icon: Shield, path: '/security-alerts' },
    { name: 'Compliance Dashboard', icon: Activity, path: '/compliance-dashboard' },
    { name: 'Audit History', icon: History, path: '/audit-history' },
    { name: 'Branding Settings', icon: Palette, path: '/branding-settings' },
    { name: 'Admin Utilities', icon: Settings, path: '/admin-utilities' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ],
  // Doctor navigation
  doctor: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Visit Management', icon: ClipboardList, path: '/visits' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'AI Scribe', icon: Mic, path: '/ai-scribe' },
    { name: 'Prescriptions', icon: Pill, path: '/prescriptions' },
    { name: 'Lab Orders', icon: TestTube, path: '/lab-orders' },
    { name: 'Medical Records', icon: FileText, path: '/medical-records' },
    { name: 'Lab Entries', icon: FlaskConical, path: '/lab-entries' },
    { name: 'FHIR Viewer', icon: FileJson, path: '/fhir-viewer' },
    { name: 'Admissions', icon: BedDouble, path: '/admissions' },
    { name: 'Discharge Summary', icon: FileText, path: '/discharge-summary' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ],
  // Reception navigation
  reception: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Quick Registration', icon: QrCode, path: '/quick-registration' },
    { name: 'Visit Management', icon: ClipboardList, path: '/visits' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'Admissions', icon: BedDouble, path: '/admissions' },
    { name: 'Billing', icon: Receipt, path: '/billing' },
    { name: 'Discharge Summary', icon: FileText, path: '/discharge-summary' },
    { name: 'Referral Triage', icon: Activity, path: '/referral-triage' },
    { name: 'Provider Management', icon: UserPlus, path: '/provider-management' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ],
  // Lab Technician navigation
  lab_tech: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Lab Orders', icon: TestTube, path: '/lab-orders' },
    { name: 'Lab Entries', icon: FlaskConical, path: '/lab-entries' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Visit Management', icon: ClipboardList, path: '/visits' },
    { name: 'Medical Records', icon: FileText, path: '/medical-records' },
    { name: 'Admissions', icon: BedDouble, path: '/admissions' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ],
  // Fallback for legacy roles (nurse, receptionist, patient)
  nurse: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Visit Management', icon: ClipboardList, path: '/visits' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Prescriptions', icon: Pill, path: '/prescriptions' },
    { name: 'Lab Orders', icon: TestTube, path: '/lab-orders' },
    { name: 'Medical Records', icon: FileText, path: '/medical-records' },
    { name: 'Lab Entries', icon: FlaskConical, path: '/lab-entries' },
    { name: 'Admissions', icon: BedDouble, path: '/admissions' },
    { name: 'Discharge Summary', icon: FileText, path: '/discharge-summary' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ],
  receptionist: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Visit Management', icon: ClipboardList, path: '/visits' },
    { name: 'Patients', icon: Users, path: '/patients' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'Billing', icon: Receipt, path: '/billing' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ],
  patient: [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { name: 'Appointments', icon: Calendar, path: '/appointments' },
    { name: 'Billing', icon: Receipt, path: '/billing' },
    { name: 'Notifications', icon: Bell, path: '/notifications', hasBadge: true },
  ]
};

export default function DashboardLayout({ user, onLogout, hospital }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Get branding from context or props
  const branding = hospital?.branding || {
    primary_color: '#4A238F',
    secondary_color: '#7C3AED',
    logo_url: '/logo-complete.png',
    tagline: 'Paperless Productive Practice'
  };
  const hospitalName = hospital?.name || 'Zesta Healthcare';

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch unread count');
      }
    };

    fetchUnreadCount();
    // Poll every 30 seconds for new notifications
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Refresh count when navigating away from notifications page
  useEffect(() => {
    if (location.pathname !== '/notifications') {
      api.get('/notifications/unread-count')
        .then(response => setUnreadCount(response.data.count))
        .catch(() => {});
    }
  }, [location.pathname]);

  // Get navigation based on user role (with safety check)
  const navigation = user?.role ? (NAVIGATION_CONFIG[user.role] || NAVIGATION_CONFIG.patient) : NAVIGATION_CONFIG.admin;

  // Add badge count to notifications
  const navWithBadges = navigation.map(item => ({
    ...item,
    badge: item.hasBadge ? unreadCount : undefined
  }));

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: branding.primary_color }}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center py-4 px-3 border-b border-white/20">
            <img 
              src={branding.logo_url || '/logo-complete.png'} 
              alt={hospitalName} 
              className="h-12 w-12 object-contain mb-1 bg-white rounded-lg p-1"
            />
            <h1 className="text-sm font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
              {hospitalName}
            </h1>
            {branding.tagline && (
              <p className="text-[8px] text-white/70 tracking-wider italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                {branding.tagline}
              </p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto custom-scrollbar">
            {navWithBadges.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-purple-700 text-white'
                      : 'text-purple-100 hover:bg-purple-700/50 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="flex-1 text-left">{item.name}</span>
                  {item.badge > 0 && (
                    <span className="ml-auto bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t border-purple-500 p-4">
            <div className="mb-3">
              <p className="text-sm font-medium text-white">{user?.full_name || 'User'}</p>
              <p className="text-xs text-purple-200 capitalize">{user?.role?.replace('_', ' ') || 'Guest'}</p>
            </div>
            <button
              onClick={() => navigate('/security-settings')}
              data-testid="security-settings-link"
              className="w-full flex items-center gap-2 px-3 py-2 mb-2 text-sm text-purple-100 hover:bg-purple-700 rounded-md transition-colors"
            >
              <Shield className="h-4 w-4" />
              Security Settings
            </button>
            <Button
              onClick={onLogout}
              data-testid="logout-button"
              className="w-full bg-purple-700 hover:bg-purple-600 text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-14 md:h-16 items-center gap-4 border-b border-slate-200 bg-white/80 backdrop-blur-md px-4 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {/* Break-Glass Emergency Access Button */}
            <BreakGlassButton user={user} />
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-subtle" />
              <span className="text-xs md:text-sm text-slate-600">Online</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8 pb-20 md:pb-8 safe-area-padding">
          <Outlet />
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-slate-200 safe-area-padding">
          <div className="flex justify-around items-center h-16">
            <button
              onClick={() => navigate('/')}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${location.pathname === '/' ? 'text-[#4A238F]' : 'text-slate-500'}`}
            >
              <LayoutDashboard className="h-5 w-5" />
              <span className="text-xs">Home</span>
            </button>
            <button
              onClick={() => navigate('/patients')}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${location.pathname === '/patients' ? 'text-[#4A238F]' : 'text-slate-500'}`}
            >
              <Users className="h-5 w-5" />
              <span className="text-xs">Patients</span>
            </button>
            <button
              onClick={() => navigate('/appointments')}
              className={`flex flex-col items-center gap-1 px-3 py-2 ${location.pathname === '/appointments' ? 'text-[#4A238F]' : 'text-slate-500'}`}
            >
              <Calendar className="h-5 w-5" />
              <span className="text-xs">Appts</span>
            </button>
            <button
              onClick={() => navigate('/notifications')}
              className={`flex flex-col items-center gap-1 px-3 py-2 relative ${location.pathname === '/notifications' ? 'text-[#4A238F]' : 'text-slate-500'}`}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 bg-rose-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <span className="text-xs">Alerts</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
}